import { validateField, validateForm, rerenderValidationNew } from "../utils/validation";
import { DEMO_REQUEST_VALIDATION_RULES } from "../validation/apiDemoRequestValidationRules";
import { showToast } from "../_components/toast";
import { trans } from "../i18n/i18n";
import { submitDemoRequest } from "../api/demo";
import { Toast } from "../consts/toast";
import { trackEvent } from "../tracking/gtm";
import { initBusinessIndustryFields } from "../form/businessIndustry";
import { getRecaptchaToken } from "../utils/recaptcha";

let businessFieldManager = {
    resolveInput(input) {
        return { targetInput: input, displayInput: input };
    },
    getDisplayInput() {
        return null;
    },
};

function getDemoRequestWhitelist(form) {
    const base = Object.fromEntries(
        Object.keys(DEMO_REQUEST_VALIDATION_RULES).map(key => [key, true])
    );

    const isLoggedIn = document.documentElement.classList.contains('is-logged-in');

    if (isLoggedIn) {
        delete base.first_name;
        delete base.last_name;
        delete base.email;
    }

    return base;
}

document.addEventListener("DOMContentLoaded", function () {
    const form = document.getElementById("demo-form");

    if (!form) return;

    businessFieldManager = initBusinessIndustryFields(form);

    const handleFormInput = (event) => handleDemoFormInput(event, businessFieldManager);

    form.addEventListener("input", handleFormInput);
    form.addEventListener("change", handleFormInput);
    form.addEventListener("submit", submitDemoRequestForm);
});

/**
 * Handles validation on individual form fields when they are changed or updated.
 *
 * @param {Event} e - The input or change event.
 */
function handleDemoFormInput(e, manager = businessFieldManager) {
    const inputEl = e.target;
    const form = e.currentTarget || inputEl.closest("form");

    const { targetInput, displayInput } = manager.resolveInput(inputEl);

    if (!targetInput || !displayInput) {
        return;
    }

    const allErrors = validateField(form, targetInput, DEMO_REQUEST_VALIDATION_RULES, getDemoRequestWhitelist(form));
    const fieldErrors = allErrors?.[targetInput.name] || [];

    rerenderValidationNew(displayInput, fieldErrors);
}

/**
 * Disables or enables all form fields inside the form.
 *
 * @param {HTMLFormElement} form - The form element.
 * @param {boolean} isDisabled - Whether to disable (true) or enable (false) the fields.
 */
function setFormDisabledState(form, isDisabled) {
    const elements = form.querySelectorAll("input, select, textarea, button");
    elements.forEach((el) => {
        el.disabled = isDisabled;
    });
}

/**
 * Handles form submission: validates the entire form, sends data via API,
 * displays success or error messages, toggles spinner and form visibility.
 *
 * @param {SubmitEvent} e - The form submit event.
 * @returns {Promise<void>}
 */
async function submitDemoRequestForm(e) {
    e.preventDefault();

    const form = e.target;
    const spinner = document.getElementById("demoFormSpinner")?.parentElement;
    const recaptchaActionName = form.dataset.recaptchaActionName;

    const hasErrors = validateForm(form, DEMO_REQUEST_VALIDATION_RULES, getDemoRequestWhitelist(form));

    if (hasErrors) {
        const message = trans("Please correct the errors in the form before submitting.");
        showToast(message, Toast.ERROR);
        return;
    }

    if (recaptchaActionName === "undefined") {
        console.error("Missing reCAPTCHA action name.");
        showToast(trans("Failed to submit call request. Please try again later."), Toast.ERROR);
        return;
    }

    // Create FormData before disabling form
    const formData = new FormData(form);

    // Disable form and show spinner
    setFormDisabledState(form, true);
    spinner?.classList.remove("d-none");

    try {
        formData.append("recaptcha_token", await getRecaptchaToken(recaptchaActionName));
        await submitDemoRequest(formData);

        form.classList.add("d-none");
        document.getElementById("demo-form-thankyou").classList.remove("d-none");

        trackEvent("form_request_call", {"email": formData.get("email")});
    } catch (error) {
        const errors = error?.response?.data?.errors;
        if (errors) {
            Object.entries(errors).forEach(([field, msgs]) => {
                const messages = Array.isArray(msgs) ? msgs : [msgs];
                const displayInput = businessFieldManager.getDisplayInput(field) || document.getElementById(field);

                if (displayInput) {
                    rerenderValidationNew(displayInput, messages);
                }
            });
        }

        // Re-enable form on error
        setFormDisabledState(form, false);
    } finally {
        spinner?.classList.add("d-none");
    }
}
